import { useState } from "react";
import { cn } from "@/utils/cn";

interface PencomDateFieldProps {
    id: string;
    label: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    className?: string;
    disabled?: boolean;
    min?: string;
    max?: string;
    error?: boolean;
    labelClassName?: string;
    helperText?: string;
    descriptionText?: string;
}

const PencomDateField = ({
    id,
    label,
    placeholder,
    value,
    onChange,
    className,
    disabled,
    min,
    max,
    error,
    helperText,
    labelClassName,
    descriptionText,
    ...props
}: PencomDateFieldProps) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
    };

    // Native <input type="date"> always paints its own "dd/mm/yyyy" segment
    // placeholder when empty and ignores the `placeholder` attribute. Cover
    // it with our own placeholder text at rest, and let the native segments
    // show through once the user focuses the field to enter a date.
    const showPlaceholderOverlay = !value && !isFocused;

    return (
        <div className="flex flex-col gap-y-1">
            <label htmlFor={id} className={cn("font-clash-display text-medium-dark-grey text-md font-semibold", labelClassName)}>
                {label}
            </label>
            <p className="font-clash-display font-medium text-xs text-medium-grey tracking-[-0.06px]">
                {descriptionText}
            </p>
            <div className="relative">
                <input
                    {...props}
                    id={id}
                    type="date"
                    disabled={disabled}
                    value={value}
                    onChange={handleDateChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    className={cn("w-full items-start px-4 font-regular text-xs font-clash-display text-medium-dark-grey py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-button focus:border-transparent", error ? "border-red-500" : "border-stroke", className)}
                />
                {showPlaceholderOverlay && (
                    <span className="absolute inset-[1px] rounded-[7px] flex items-center px-4 bg-white font-regular text-xs font-clash-display text-medium-grey pointer-events-none">
                        {placeholder || "Select date"}
                    </span>
                )}
                <img
                    src="/assets/icons/calendar.svg"
                    alt="calendar"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                />
            </div>
            {
                helperText && (
                    <p className="text-red-500 text-md font-clash-display">{helperText}</p>
                )
            }
        </div >
    );
};

export default PencomDateField;
